/**
 * Form pipeline step 1: Database schema generation for form submissions
 */

import {
  getAllFormSchemaFields,
  hasDynamicFields
} from '@core-engine/schema/field-helpers/index.js'
import { formFieldToDrizzleType } from '@core-engine/schema/type-mappers/index.js'
import type { FormSchema, GeneratedFile, GeneratorOptions } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths.js'

interface StepResult {
  files: GeneratedFile[]
}

export function generateFormDatabase(
  schema: FormSchema,
  dbDir: string,
  options: GeneratorOptions
): StepResult {
  const outputNamespace = resolveExtensionNamespace(schema.name, options.outputNamespace)
  const tableName = `${toCamelCase(schema.name)}Submissions`
  const fields = getAllFormSchemaFields(schema)
  const includeDynamic = hasDynamicFields(schema)

  const requiredImports = new Set<string>(['pgTable', 'timestamp', 'text', 'uuid'])
  if (includeDynamic) {
    requiredImports.add('jsonb')
  }

  const fieldDefs = fields
    .map((field) => {
      const drizzleType = formFieldToDrizzleType(field, requiredImports)
      return `  ${field.name}: ${drizzleType},`
    })
    .join('\n')

  const customFieldsCol = includeDynamic
    ? '\n  customFields: jsonb().$type<Record<string, unknown>>(),'
    : ''

  const imports = Array.from(requiredImports).sort().join(',\n  ')
  const content = `import { sql } from 'drizzle-orm'
import {
  ${imports}
} from 'drizzle-orm/pg-core'

export const ${tableName} = pgTable('${toPascalCase(schema.name)}Submissions', {
  id: uuid().defaultRandom().primaryKey().notNull(),
${fieldDefs}${customFieldsCol}
  ipAddress: text(),
  userAgent: text(),
  submittedAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull()
})
`

  return {
    files: [createGeneratedFile(`${dbDir}/${outputNamespace}/${schema.name}.ts`, content)]
  }
}
